import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { isThisWeek, isToday, isAfterThisWeek, isYesterday, isAfterToday } from 'helpers/date';
import { subtractTimeZone } from 'helpers';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { getFilteredTaskAggregateSelector } from 'store/selectors/taskAggregate';
import { getIsTaskFiltersApplied } from 'store/selectors/transactionTasks';

import { tasksStatusesIds } from 'settings/constants/transactionTasks';

import { Wrapper } from 'components';
import { FormDetails } from 'pages/Workshop/Forms/components/FormDetails';
import { CollapsibleTable, WorkshopTableWrapper } from 'pages/Workshop/common';
import { handleViewForm } from 'utils';

import styles from './styles.module.scss';

import { TransactionDrawer } from 'pages/Workshop/Transactions/TransactionOverview/components/TransactionDrawer';

import { useManageTasksLists } from 'pages/Workshop/Tasks/components/TaskTabs/useManageTasksList';

const TasksList = (props) => {
  const {
    className,
    isAggregatedTasks,
    fetchAggregateTasks,
    showSelection,
    handleSelectTask,
    selectedTasks,
    showSelectAll,
  } = props;

  const history = useHistory();

  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    tasks,
    isPending: isTaskListLoading,
    isArchive,
  } = useSelector(getFilteredTaskAggregateSelector);

  const filtersApplied = useSelector(getIsTaskFiltersApplied);

  const isFilter = filtersApplied && !isArchive;
  const filteredStatuses = isAggregatedTasks
    ? [tasksStatusesIds.overdue, tasksStatusesIds.done, tasksStatusesIds.na]
    : [tasksStatusesIds.overdue];

  const manageTasksProps = {
    showSelection,
    handleSelectTask,
    selectedTasks,
    showSelectAll,
    isAggregatedTasks,
    tableClassName: styles.taskTable,
  };

  const {
    tableProps,
    transactionDrawerProps,
    openTransactionDrawer,
    setOpenTransactionDrawer,
    formProcessDetail,
    setFormProcessDetail,
  } = useManageTasksLists(manageTasksProps);

  useEffect(() => {
    setLoading(false);
  }, [taskList]);

  useEffect(() => {
    if (tasks && !isTaskListLoading) {
      const formattedTasks = tasks?.map((task) => {
        if (task.formProcessDetails) {
          const formProcessDetails = task.formProcessDetails;
          if (formProcessDetails.taskName) {
            task.Title = task.formProcessDetails.taskName;
          }
          if (formProcessDetails.participants?.length) {
            task.AssigneeList = formProcessDetails.participants;
          }
        }
        return task;
      });

      if (!filtersApplied && !isAggregatedTasks) {
        const activeTasks = formattedTasks.filter(
          (item) => ![tasksStatusesIds.done, tasksStatusesIds.na].includes(item.Status),
        );
        setTaskList(activeTasks);
      } else {
        setTaskList(formattedTasks);
      }
    }
  }, [isArchive, isTaskListLoading, tasks, filtersApplied]);

  useEffect(() => {
    setLoading(false);
  }, [taskList]);

  function overDueTasks() {
    return taskList.filter((item) => item?.Status === tasksStatusesIds.overdue);
  }

  function todayTasks() {
    return taskList.filter((item) => {
      let dueDate = item.DueDate ? subtractTimeZone(item.DueDate, 'M/DD/YYYY') : 'N/A';

      return isToday(dueDate) && !filteredStatuses.includes(item?.Status);
    });
  }

  function yesterdayTasks() {
    return taskList.filter((item) => {
      let dueDate = item.DueDate ? subtractTimeZone(item.DueDate, 'M/DD/YYYY') : 'N/A';
      return isYesterday(dueDate) && item?.Status !== tasksStatusesIds.overdue;
    });
  }

  function thisWeekTasks() {
    return taskList.filter((item) => {
      let dueDate = item.DueDate ? subtractTimeZone(item.DueDate, 'M/DD/YYYY') : 'N/A';

      return (
        isThisWeek(dueDate) && isAfterToday(dueDate) && !filteredStatuses.includes(item?.Status)
      );
    });
  }

  function upcomingTasks() {
    return taskList.filter((item) => {
      let dueDate = item.DueDate ? subtractTimeZone(item.DueDate, 'M/DD/YYYY') : 'N/A';

      return isAfterThisWeek(dueDate) && !filteredStatuses.includes(item?.Status);
    });
  }

  function doneTasks() {
    return taskList.filter((item) => item.Status === tasksStatusesIds.done);
  }

  function naTasks() {
    return taskList.filter((item) => item.Status === tasksStatusesIds.na);
  }

  const isCollapseUpcomingTasks = useMemo(() => {
    // When upcoming is the only category, expand the table.

    const totalTasksCount =
      overDueTasks()?.length +
      todayTasks()?.length +
      thisWeekTasks()?.length +
      upcomingTasks()?.length;

    return totalTasksCount !== upcomingTasks()?.length;
  }, [taskList]);

  return (
    <Wrapper isPending={isTaskListLoading}>
      <div className={classNames(styles.tasksList, className)}>
        {isFilter ? (
          <WorkshopTableWrapper data={[taskList]} emptyText="No Tasks.">
            <CollapsibleTable
              title={`${taskList.length} Tasks`}
              dataSource={taskList}
              disableCollapse
              {...tableProps()}
            />
          </WorkshopTableWrapper>
        ) : isArchive && !isAggregatedTasks ? (
          <WorkshopTableWrapper data={[doneTasks(), naTasks()]} emptyText="No Tasks.">
            <CollapsibleTable
              showCollapseIcon
              title="Done"
              defaultCollapse
              dataSource={doneTasks()}
              {...tableProps(isFilter ? taskList : doneTasks())}
            />
            <CollapsibleTable
              showCollapseIcon
              title="N/A"
              defaultCollapse
              dataSource={naTasks()}
              {...tableProps(isFilter ? taskList : naTasks())}
            />
          </WorkshopTableWrapper>
        ) : (
          <WorkshopTableWrapper
            data={[
              overDueTasks(),
              todayTasks(),
              thisWeekTasks(),
              upcomingTasks(),
              yesterdayTasks(),
              ...(isAggregatedTasks ? [doneTasks(), naTasks()] : []),
            ]}
            emptyText="No Tasks."
          >
            <CollapsibleTable
              showCollapseIcon
              title="Overdue"
              dataSource={overDueTasks()}
              {...tableProps(isFilter ? taskList : overDueTasks())}
            />
            <CollapsibleTable
              showCollapseIcon
              title="Yesterday"
              dataSource={yesterdayTasks()}
              {...tableProps(isFilter ? taskList : yesterdayTasks())}
            />

            <CollapsibleTable
              showCollapseIcon
              title="Today"
              dataSource={todayTasks()}
              {...tableProps(isFilter ? taskList : todayTasks())}
            />
            <CollapsibleTable
              title="This Week"
              showCollapseIcon
              dataSource={thisWeekTasks()}
              {...tableProps(isFilter ? taskList : thisWeekTasks())}
            />
            <CollapsibleTable
              title="Upcoming"
              defaultCollapse={isCollapseUpcomingTasks}
              showCollapseIcon
              dataSource={upcomingTasks()}
              {...tableProps(isFilter ? taskList : upcomingTasks())}
            />

            {isAggregatedTasks ? (
              <>
                <CollapsibleTable
                  showCollapseIcon
                  title="Done"
                  dataSource={doneTasks()}
                  {...tableProps(isFilter ? taskList : doneTasks())}
                />
                <CollapsibleTable
                  showCollapseIcon
                  title="N/A"
                  dataSource={naTasks()}
                  {...tableProps(isFilter ? taskList : naTasks())}
                />
              </>
            ) : (
              <></>
            )}
          </WorkshopTableWrapper>
        )}
      </div>

      {formProcessDetail && (
        <FormDetails
          selectedFormProcess={formProcessDetail}
          onClose={() => setFormProcessDetail(undefined)}
          onHandleViewForm={(url, type) => handleViewForm(history, url, type)}
          onCancelForm={fetchAggregateTasks}
        />
      )}

      {/* Transaction Drawer */}
      {openTransactionDrawer && (
        <TransactionDrawer
          openDrawer={openTransactionDrawer}
          setOpenDrawer={setOpenTransactionDrawer}
          {...transactionDrawerProps}
        />
      )}
    </Wrapper>
  );
};

TasksList.propTypes = {
  className: PropTypes.string,
  taskTableClassName: PropTypes.string,
  isAggregatedTasks: PropTypes.bool,
  fetchAggregateTasks: PropTypes.func,
  showSelection: PropTypes.bool,
  handleSelectTask: PropTypes.func,
  selectedTasks: PropTypes.arrayOf(PropTypes.number),
  showSelectAll: PropTypes.bool,
};

TasksList.defaultProps = {
  className: '',
  taskTableClassName: '',
  isAggregatedTasks: false,
  fetchAggregateTasks: () => {},
  selectedTasks: [],
  showSelectAll: false,
};

export default TasksList;
